// AIDialerScreeners.jsx — list of saved AI screener bots for the tenant.
//
// Surfaced as:
//   • Standalone route /ai-dialer/screeners
//   • Tab inside the AI Dialer hub (?tab=screeners)
//
// Each row supports Edit (→ trainer), Test (audio modal inline), Delete
// (confirm modal). The empty state from the Campaigns tab routes here so
// users see what they already have before training another bot.

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bot, Plus, Loader2, Trash2, Edit3, Play, Volume2, Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiDel } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import BotAudioTest from '../components/dialer/BotAudioTest.jsx';
import ConfirmModal from '../components/dialer/ConfirmModal.jsx';

const STOCK_VOICES = [
  { id: 'pNInz6obpgDQGcFmaJgB', label: 'Adam (warm, male)' },
  { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah (friendly, female)' },
  { id: 'CYw3kZ02Hs0563khs1Fj', label: 'Dave (calm, male)' },
  { id: 'XrExE9yKIg1WjnnlVkGX', label: 'Matilda (energetic, female)' },
];

const STATUS_PILL = {
  ready: { label: 'Ready', bg: 'bg-green-400/10', color: 'text-green-300', dot: 'bg-green-400' },
  draft: { label: 'Draft', bg: 'bg-white/5',      color: 'text-white/60',  dot: 'bg-white/50' },
};

function voiceLabel(bot) {
  if (bot?.voiceLabel) return bot.voiceLabel;
  const v = STOCK_VOICES.find((s) => s.id === bot?.voiceId);
  if (v) return v.label;
  if (bot?.voiceSource === 'clone') return 'Cloned voice';
  return bot?.voiceId || '—';
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const today = new Date();
    const isToday = dt.toDateString() === today.toDateString();
    if (isToday) return `Today ${dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    return dt.toLocaleDateString([], { month: 'short', day: 'numeric', year: dt.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
  } catch {
    return '—';
  }
}

export default function ScreenersList({ embedded = false }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState(null); // { id, name } | null
  const [testingBotId, setTestingBotId] = useState(null);

  const { data: botsData, isLoading, isError, refetch } = useQuery({
    queryKey: ['dialer-v1-bots'],
    queryFn: () => apiGet('/dialer/v1/bots'),
  });
  const bots = botsData?.data || [];

  // Tally campaigns per bot — match by persona.name since v1 campaigns
  // snapshot persona fields rather than referencing the bot by id.
  const { data: campsData } = useQuery({
    queryKey: ['dialer-v1-campaigns'],
    queryFn: () => apiGet('/dialer/v1/campaigns'),
  });
  const campsByBotName = useMemo(() => {
    const map = new Map();
    for (const c of campsData?.data || []) {
      const n = c?.persona?.name;
      if (!n) continue;
      map.set(n, (map.get(n) || 0) + 1);
    }
    return map;
  }, [campsData]);

  const deleteMut = useMutation({
    mutationFn: (id) => apiDel(`/dialer/v1/bots/${id}`),
    onSuccess: () => {
      toast.success('Screener deleted');
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey: ['dialer-v1-bots'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Delete failed'),
  });

  const goNew = () => nav('/ai-dialer/bots/train');
  const goEdit = (id) => nav(`/ai-dialer/bots/train?id=${id}`);

  const header = (
    <div className="flex items-center justify-between">
      <div>
        <div className="text-lg font-semibold text-white">AI Screeners</div>
        <div className="text-xs text-white/50">
          Your trained AI voices. Edit content, test live audio, or wire them into a campaign.
        </div>
      </div>
      <button onClick={goNew} className="btn-primary flex items-center gap-2">
        <Plus className="w-4 h-4" /> New Screener
      </button>
    </div>
  );

  let body;
  if (isLoading) {
    body = <div className="panel p-12 flex justify-center"><Loader2 className="w-8 h-8 text-civix-purple animate-spin" /></div>;
  } else if (isError) {
    body = (
      <div className="panel p-10 text-center">
        <div className="text-white/80 mb-2">Couldn't load your screeners.</div>
        <button onClick={() => refetch()} className="btn-secondary">Try again</button>
      </div>
    );
  } else if (bots.length === 0) {
    body = (
      <div className="panel p-10 text-center">
        <Bot className="w-12 h-12 text-civix-purple/40 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">No screeners yet</h3>
        <p className="text-white/50 text-sm mb-6 max-w-md mx-auto">
          Train your first AI voice. You can upload a script, pick a stock voice or clone your own, and test it live before launching a campaign.
        </p>
        <button onClick={goNew} className="btn-primary inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Train New Screener
        </button>
      </div>
    );
  } else {
    body = (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {bots.map((b) => {
          const status = STATUS_PILL[b.status] || STATUS_PILL.draft;
          const inUse = campsByBotName.get(b.name) || 0;
          return (
            <div key={b._id} className="panel p-5 hover:border-civix-purple/30 transition-all">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg civix-gradient flex items-center justify-center shrink-0">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-semibold text-white truncate">{b.name || 'Untitled screener'}</h3>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${status.bg} ${status.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                      {status.label}
                    </span>
                  </div>
                  <div className="text-xs text-white/50 flex items-center gap-1.5">
                    <Volume2 className="w-3 h-3" /> {voiceLabel(b)}
                  </div>
                </div>
              </div>

              {b.description && (
                <div className="text-xs text-white/60 line-clamp-2 mb-3">{b.description}</div>
              )}

              <div className="grid grid-cols-3 gap-2 text-xs mb-4">
                <Stat label="Questions" value={(b.qualifyingQuestions || []).length} />
                <Stat label="Campaigns" value={inUse} />
                <Stat label="Last edited" value={fmtDate(b.updatedAt || b.createdAt)} />
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setPendingDelete({ id: b._id, name: b.name })}
                  className="btn-sm btn-ghost text-red-400 flex items-center gap-1"
                  disabled={inUse > 0}
                  title={inUse > 0 ? `In use by ${inUse} campaign${inUse === 1 ? '' : 's'} — remove from campaigns first` : 'Delete this screener'}
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
                <button
                  onClick={() => setTestingBotId(b._id)}
                  className="btn-sm btn-ghost flex items-center gap-1"
                  title="Talk to this bot live with your mic"
                >
                  <Play className="w-3 h-3" /> Test
                </button>
                <button
                  onClick={() => goEdit(b._id)}
                  className="btn-sm btn-primary flex items-center gap-1"
                >
                  <Edit3 className="w-3 h-3" /> Edit
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const inner = (
    <div className="space-y-4">
      {header}
      {body}
      {pendingDelete && (
        <ConfirmModal
          title="Delete this screener?"
          body={
            <>
              You're about to permanently delete{' '}
              <span className="text-white">"{pendingDelete.name || 'this screener'}"</span>.
              Campaigns that already snapshotted this persona keep working — but you won't be able to edit it again or reuse it in a new campaign.
            </>
          }
          confirmLabel="Delete screener"
          danger
          busy={deleteMut.isPending}
          onConfirm={() => deleteMut.mutate(pendingDelete.id)}
          onCancel={() => !deleteMut.isPending && setPendingDelete(null)}
        />
      )}
      {testingBotId && (
        <BotAudioTest botId={testingBotId} onClose={() => setTestingBotId(null)} />
      )}
    </div>
  );

  if (embedded) return inner;

  return (
    <div className="space-y-4">
      <TopBar
        title="AI Screeners"
        subtitle="Every AI voice you've trained for the dialer — edit, test live, or delete."
      />
      {inner}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/5 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="text-sm font-semibold text-white truncate">{value}</div>
    </div>
  );
}
